import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Settings, Eye, Edit, Plus } from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const AppConfig = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const limit = 10;

  const canView = hasPermission('appconfig', 'view');

  const { data, isLoading, error } = useQuery(
    ['appConfigs', page],
    async () => {
      const response = await api.get('/publicRidesAppConfig', {
        params: { page, limit }
      });
      return response;
    },
    {
      enabled: canView,
      keepPreviousData: true,
      refetchOnWindowFocus: false
    }
  );

  if (!canView) {
    return (
      <div className="empty-state">
        <Settings className="empty-state-icon" />
        <h3 className="empty-state-title">Access Denied</h3>
        <p className="empty-state-description">
          You don't have permission to view app configurations.
        </p>
      </div>
    );
  }

  if (error) {
    toast.error('Failed to load app configurations');
  }

  const configs = data?.data?.data?.configs || [];
  const pagination = data?.data?.data?.pagination || {};

  console.log('Configs from API:', configs);

  // Separate configs by APP_NAME
  const customerConfigs = configs.filter(config => 
    config.APP === 'PUBLICRIDE_CUSTOMER_APP' || config.APP === 'CUSTOMER'
  );
  const driverConfigs = configs.filter(config => 
    config.APP === 'NOT_PUBLICRIDE_DRIVER_APP' || config.APP === 'DRIVER'
  );

  const regionModeLabel = (config) => {
    const services = config.HOME_SCREEN_CONFIG?.find(s => s.key === 'services')?.items || [];
    const keys = services.map(i => i.key);
    const hasActing = keys.includes('acting_driver');
    const hasNormal = keys.some(k => k !== 'acting_driver');
    if (hasActing && !hasNormal) return { label: 'Acting Driver Only', color: 'purple' };
    if (!hasActing && hasNormal) return { label: 'Normal Ride Only', color: 'blue' };
    if (hasActing && hasNormal) return { label: 'Both Services', color: 'green' };
    return null;
  };

  const ConfigCard = ({ config }) => {
    const mode = regionModeLabel(config);
    return (
      <div className="card mb-4">
        <div className="card-body">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="text-lg font-semibold text-gray-900">{config.APP_NAME || config.APP || 'N/A'}</h4>
                {config.regionCode && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-600 border border-gray-300">
                    {config.regionCode}
                  </span>
                )}
                {config.state && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border
                    ${config.state === 'DEFAULT' ? 'bg-gray-50 text-gray-500 border-gray-200' : 'bg-primary-50 text-primary-700 border-primary-200'}`}>
                    {config.state === 'DEFAULT' ? 'DEFAULT state' : config.state}
                  </span>
                )}
                {mode && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                    ${mode.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                      mode.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                      'bg-green-100 text-green-700'}`}>
                    {mode.label}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-1">{config.COMPANYNAME || 'N/A'}</p>
            </div>
            <div className="flex items-center">
              <Link
                to={`/config/${config._id}`}
                className="text-primary-600 hover:text-primary-900 p-2 flex items-center gap-2"
                title="View Details"
              >
                <Eye className="h-5 w-5" />
                <span>View Details</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">App Configurations</h1>
              <p className="page-subtitle">Manage customer and driver app settings per region</p>
            </div>
            <button
              onClick={() => navigate('/config/create')}
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Create Config
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="loading-spinner"></div>
        </div>
      ) : configs.length === 0 ? (
        <div className="empty-state">
          <Settings className="empty-state-icon" />
          <h3 className="empty-state-title">No configurations found</h3>
          <p className="empty-state-description">No app configurations available.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Customer Configurations Column */}
          <div>
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Customer Configurations</h3>
              </div>
              <div className="card-body">
                {customerConfigs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No customer configurations found
                  </div>
                ) : (
                  customerConfigs.map(config => (
                    <ConfigCard key={config._id} config={config} />
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Driver Configurations Column */}
          <div>
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Driver Configurations</h3>
              </div>
              <div className="card-body">
                {driverConfigs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No driver configurations found
                  </div>
                ) : (
                  driverConfigs.map(config => (
                    <ConfigCard key={config._id} config={config} />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.total > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((pagination.current - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.current * pagination.limit, pagination.totalRecords)} of{' '}
            {pagination.totalRecords} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage(pagination.current - 1)}
              disabled={pagination.current === 1}
              className="btn btn-outline btn-sm"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(pagination.current + 1)}
              disabled={pagination.current === pagination.total}
              className="btn btn-outline btn-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppConfig;